/**
 * Admin route subtree.
 *
 * React Router v7 routes for the entire admin panel, mounted as children of the
 * app's single router (see routes.tsx). It used to own a `createBrowserRouter`,
 * but the Vite SPA has exactly one router at the root — a nested RouterProvider
 * throws "You cannot render a <Router> inside another <Router>". Exporting the
 * route tree lets the app router host /admin/* directly.
 *
 * All routes lazy-load their page components. AdminLayout mounts ONCE at the
 * subtree root, providing permission checks, header, and sidebar; pages render
 * bare content via <Outlet />.
 */

import { lazy, Suspense } from "react"
import { Navigate, Outlet, type RouteObject, useParams } from "react-router"

import { AdminPageLoading } from "./components/AdminDynamicLoader"

// Lazy so the admin shell never enters the customer entry chunk — adminRoutes is
// imported eagerly by the app route table, but its chrome and pages load only
// when /admin is visited.
const AdminLayout = lazy(() =>
  import("./components/AdminLayout").then((m) => ({ default: m.AdminLayout }))
)

// ============================================================
// Layout Route
// ============================================================

/**
 * Root layout for all /admin/* routes.
 *
 * AdminLayout mounts once here — it provides AdminPermissionProvider,
 * the header, sidebar, and PBAC enforcement.
 * Pages render bare content; they no longer wrap themselves in AdminLayout.
 */
function AdminLayoutRoute() {
  return (
    <Suspense fallback={<AdminPageLoading />}>
      <AdminLayout>
        <Suspense fallback={<AdminPageLoading />}>
          <Outlet />
        </Suspense>
      </AdminLayout>
    </Suspense>
  )
}

// Redirect helper for dynamic org detail index
function OrgDetailRedirect() {
  const { orgId } = useParams<{ orgId: string }>()
  return <Navigate to={`/admin/organizations/${orgId ?? ""}/overview`} replace />
}

// ============================================================
// Router
// ============================================================

export const adminRoutes: RouteObject[] = [
  {
    path: "admin",
    Component: AdminLayoutRoute,
    children: [
      // Dashboard
      {
        index: true,
        lazy: async () => ({ Component: (await import("./pages/DashboardPage")).default })
      },

      // ── Echo (cross-org audit logs + global config) ──────
      {
        path: "echo",
        element: <Navigate to="/admin/echo/explorer" replace />
      },
      {
        path: "echo/explorer",
        lazy: async () => ({ Component: (await import("./pages/echo/EchoPage")).default })
      },
      {
        path: "echo/config",
        lazy: async () => ({ Component: (await import("./pages/echo/EchoPage")).default })
      },

      // Billing
      {
        path: "billing",
        lazy: async () => ({ Component: (await import("./pages/billing")).default })
      },

      // Credentials Encryption
      {
        path: "credentials-encryption",
        lazy: async () => ({
          Component: (await import("./pages/CredentialsEncryptionPage")).default
        })
      },

      // Feature Flags
      {
        path: "feature-flags",
        lazy: async () => ({ Component: (await import("./pages/FeatureFlagsPage")).default })
      },
      {
        path: "feature-flags/:key",
        lazy: async () => ({ Component: (await import("./pages/FeatureFlagDetailPage")).default })
      },
      // System Prompts
      {
        path: "system-prompts",
        lazy: async () => ({ Component: (await import("./pages/SystemPromptsPage")).default })
      },
      {
        path: "system-prompts/:key",
        lazy: async () => ({ Component: (await import("./pages/SystemPromptEditorPage")).default })
      },
      {
        path: "shared-workspaces",
        lazy: async () => ({ Component: (await import("./pages/SharedWorkspacesPage")).default })
      },

      // Issues
      {
        path: "issues",
        lazy: async () => ({ Component: (await import("./pages/issues")).default })
      },

      // Code Interpreter Packages
      {
        path: "code-interpreter/packages",
        lazy: async () => ({
          Component: (await import("./pages/CodeInterpreterPackagesPage")).default
        })
      },

      // Cue Skills
      {
        path: "cue",
        lazy: async () => ({
          Component: (await import("./pages/cue/CuePage")).default
        })
      },

      // Observability
      {
        path: "observability",
        lazy: async () => ({ Component: (await import("./pages/ObservabilityPage")).default })
      },

      // Redis Cache
      {
        path: "redis-cache",
        lazy: async () => ({
          Component: (await import("./pages/redis-cache/RedisCachePage")).RedisCachePage
        })
      },

      // Usage
      {
        path: "usage",
        lazy: async () => ({ Component: (await import("./pages/usage/UsagePage")).default })
      },

      // Vibe — unified operator console (Tenant Clusters + Expense as tabs)
      {
        path: "vibe",
        element: <Navigate to="/admin/vibe/tenant-clusters" replace />
      },
      {
        path: "vibe/tenant-clusters",
        lazy: async () => ({ Component: (await import("./pages/vibe")).default })
      },
      {
        path: "vibe/orphan-apps",
        lazy: async () => ({ Component: (await import("./pages/vibe")).default })
      },
      {
        path: "vibe/expense",
        lazy: async () => ({ Component: (await import("./pages/vibe")).default })
      },

      // Legacy Vibe routes → redirect to the tabbed view (preserve bookmarks)
      {
        path: "vibe-tenant-clusters",
        element: <Navigate to="/admin/vibe/tenant-clusters" replace />
      },
      {
        path: "vibe-expense",
        element: <Navigate to="/admin/vibe/expense" replace />
      },

      // Users
      {
        path: "users",
        lazy: async () => ({ Component: (await import("./pages/UsersPage")).default })
      },

      // Platform Operator Keys (cross-tenant credentials)
      {
        path: "platform-operator-keys",
        lazy: async () => ({
          Component: (await import("./pages/platform-operator-keys")).default
        })
      },

      // Vault
      {
        path: "vault",
        lazy: async () => ({ Component: (await import("./pages/VaultPage")).default })
      },

      // ── Database ─────────────────────────────────────────
      {
        path: "database",
        element: <Navigate to="/admin/database/migrations" replace />
      },
      {
        path: "database/migrations",
        lazy: async () => ({ Component: (await import("./pages/DatabasePage")).default })
      },
      {
        path: "database/sql-query",
        lazy: async () => ({ Component: (await import("./pages/DatabasePage")).default })
      },

      // ── Migrations ───────────────────────────────────────
      // MigrationsPage is self-routing: renders the correct section based on URL
      {
        path: "migrations",
        element: <Navigate to="/admin/migrations/telegram" replace />
      },
      {
        path: "migrations/webhooks",
        element: <Navigate to="/admin/migrations/gmail" replace />
      },
      {
        path: "migrations/gmail",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/slack",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/telegram",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/teams",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/twilio",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/attribution",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/notion",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/google",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/microsoft",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/pipedream",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/pipedream-reconcile",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },
      {
        path: "migrations/outlook",
        lazy: async () => ({ Component: (await import("./pages/MigrationsPage")).default })
      },

      // ── Organizations ────────────────────────────────────
      {
        path: "organizations",
        element: <Navigate to="/admin/organizations/clerk-sync" replace />
      },
      {
        path: "organizations/billing",
        lazy: async () => ({ Component: (await import("./pages/OrganizationsPage")).default })
      },
      {
        path: "organizations/clerk-sync",
        lazy: async () => ({ Component: (await import("./pages/OrganizationsPage")).default })
      },
      {
        path: "organizations/rate-limits",
        lazy: async () => ({ Component: (await import("./pages/OrganizationsPage")).default })
      },
      {
        path: "organizations/users",
        lazy: async () => ({ Component: (await import("./pages/OrganizationsPage")).default })
      },
      {
        path: "organizations/:orgId",
        Component: OrgDetailRedirect
      },
      {
        path: "organizations/:orgId/overview",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/connections",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/oauth",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/audit-logs",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/cue",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/shared-workspaces",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },
      {
        path: "organizations/:orgId/ai-evaluation-budget",
        lazy: async () => ({
          Component: (await import("./pages/org-detail/OrgDetailLayout")).default
        })
      },

      // ── Plugins ──────────────────────────────────────────
      {
        path: "plugins",
        element: <Navigate to="/admin/plugins/overview" replace />
      },
      {
        path: "plugins/overview",
        lazy: async () => ({ Component: (await import("./pages/PluginsPage")).default })
      },
      {
        path: "plugins/categories",
        lazy: async () => ({ Component: (await import("./pages/PluginsPage")).default })
      },
      {
        path: "plugins/health",
        lazy: async () => ({ Component: (await import("./pages/PluginsPage")).default })
      },
      {
        path: "plugins/cleanup",
        lazy: async () => ({ Component: (await import("./pages/PluginsPage")).default })
      },

      // ── Providers ────────────────────────────────────────
      {
        path: "providers",
        element: <Navigate to="/admin/providers/twilio" replace />
      },
      {
        path: "providers/twilio",
        lazy: async () => ({ Component: (await import("./pages/ProvidersPage")).default })
      },
      {
        path: "providers/teams",
        lazy: async () => ({ Component: (await import("./pages/ProvidersPage")).default })
      },
      {
        path: "providers/twilio/:connectionId",
        lazy: async () => ({
          Component: (await import("./pages/providers/SubaccountDetailsPage/SubaccountDetailsPage"))
            .SubaccountDetailsPage
        })
      },

      // ── Twilio Operator Console ──────────────────────────
      // Self-routing on :section so the console owns its own IA without a route
      // entry per resource — the sidebar is data, not a hand-kept route table.
      {
        path: "twilio-console",
        element: <Navigate to="/admin/twilio-console/overview" replace />
      },
      {
        path: "twilio-console/:section",
        lazy: async () => ({
          Component: (await import("./pages/twilio-console/TwilioConsolePage")).TwilioConsolePage
        })
      },
      // Subaccount detail. Three segments, so it outranks `:section` above.
      // Same page the providers route renders — it navigates back with
      // history(-1), so it works from either entry point.
      {
        path: "twilio-console/subaccounts/:connectionId",
        lazy: async () => ({
          Component: (await import("./pages/providers/SubaccountDetailsPage/SubaccountDetailsPage"))
            .SubaccountDetailsPage
        })
      },

      // ── Settings ─────────────────────────────────────────
      // SettingsPage is self-routing: renders policies/admin-users/audit based on URL
      {
        path: "settings",
        element: <Navigate to="/admin/settings/policies" replace />
      },
      {
        path: "settings/admin-users",
        lazy: async () => ({ Component: (await import("./pages/settings/SettingsPage")).default })
      },
      {
        path: "settings/audit",
        lazy: async () => ({ Component: (await import("./pages/settings/SettingsPage")).default })
      },
      {
        path: "settings/policies",
        lazy: async () => ({ Component: (await import("./pages/settings/SettingsPage")).default })
      },
      // ── System Config ────────────────────────────────────
      {
        path: "system-config",
        element: <Navigate to="/admin/system-config/cron" replace />
      },
      {
        path: "system-config/cron",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/models",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/rate_limit",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/cache",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/circuit_breaker",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/retry_policy",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/deletion_policy",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/conversation",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/public_api",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/sandbox",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },
      {
        path: "system-config/identity",
        lazy: async () => ({ Component: (await import("./pages/SystemConfigPage")).default })
      },

      // ── System Health ────────────────────────────────────
      {
        path: "system/health",
        element: <Navigate to="/admin/system/health/internal" replace />
      },
      {
        path: "system/health/internal",
        lazy: async () => ({ Component: (await import("./pages/system/HealthPage")).default })
      },
      {
        path: "system/health/external",
        lazy: async () => ({ Component: (await import("./pages/system/HealthPage")).default })
      },
      {
        path: "system/health/oauth",
        lazy: async () => ({ Component: (await import("./pages/system/HealthPage")).default })
      },
      {
        path: "system/health/cron-jobs",
        lazy: async () => ({ Component: (await import("./pages/system/HealthPage")).default })
      },

      // ── Typesense ────────────────────────────────────────
      {
        path: "typesense",
        element: <Navigate to="/admin/typesense/chat" replace />
      },
      {
        path: "typesense/chat",
        lazy: async () => ({ Component: (await import("./pages/TypesensePage")).default })
      },
      {
        path: "typesense/plugins",
        lazy: async () => ({ Component: (await import("./pages/TypesensePage")).default })
      },
      {
        path: "typesense/pipedream",
        lazy: async () => ({ Component: (await import("./pages/TypesensePage")).default })
      },
      {
        path: "typesense/pd-analytics",
        lazy: async () => ({ Component: (await import("./pages/TypesensePage")).default })
      }
    ]
  }
]
